import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { registerPush } from "@/lib/push";
import { toast } from "sonner";

const DISMISS_KEY = "aamg.enablePushDismissedAt";
const DISMISS_MS = 1000 * 60 * 60 * 24 * 7; // remember dismissal for 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function iosMajorVersion() {
  const ua = navigator.userAgent || "";
  const m = ua.match(/OS (\d+)_/);
  return m ? parseInt(m[1], 10) : 0;
}

function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

export default function EnablePushBanner() {
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    if (Notification.permission !== "default") return; // already granted or blocked
    if (!isStandalone()) return; // only when running as installed PWA

    // iOS 16.3 and below don't support web push even in PWA — hide banner there.
    if (isIOS() && iosMajorVersion() < 16) return;

    try {
      const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) return;
    } catch { /* localStorage may be unavailable in private mode */ }

    setVisible(true);
  }, []);

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const handleEnable = async () => {
    setEnabling(true);
    try {
      await registerPush();
      if (Notification.permission === "granted") {
        toast.success("Notifications enabled — you'll be alerted for new requests and messages.");
        setVisible(false);
      } else {
        toast.error("Notifications weren't enabled. You can turn them on later in iOS Settings › Notifications › AAMG.");
        dismiss();
      }
    } catch {
      toast.error("Couldn't enable notifications. Please try again from the app icon.");
    } finally {
      setEnabling(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="enable-push-banner"
      className="mb-4 rounded-xl border border-[#339999]/30 bg-gradient-to-r from-[#339999]/10 to-[#E0A96D]/10 p-3 sm:p-4 flex items-start gap-3 shadow-sm"
    >
      <div className="shrink-0 h-10 w-10 rounded-full bg-[#339999]/15 flex items-center justify-center">
        <Bell className="h-5 w-5 text-[#2A7F7F]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">
          Turn on notifications
        </div>
        <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
          {isIOS()
            ? "You'll get pinged for new procedure requests, messages, and emergency cases — even when the app is closed."
            : "Get real-time alerts for new requests, messages, and emergency cases."}
        </div>
        <div className="flex flex-wrap gap-2 mt-2.5">
          <button
            type="button"
            data-testid="enable-push-btn"
            onClick={handleEnable}
            disabled={enabling}
            className="text-xs font-medium px-3 py-1.5 rounded-md bg-[#339999] text-white hover:bg-[#2A7F7F] transition disabled:opacity-60"
          >
            {enabling ? "Enabling…" : "Enable notifications"}
          </button>
          <button
            type="button"
            data-testid="dismiss-push-btn"
            onClick={dismiss}
            className="text-xs font-medium px-3 py-1.5 rounded-md text-slate-600 hover:bg-slate-100 transition"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="close-push-banner-btn"
        onClick={dismiss}
        className="shrink-0 text-slate-400 hover:text-slate-600 transition"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
